import React, { useState, useEffect } from "react";
import { Link, useMatch, useLocation } from 'react-router-dom';
import { useAuth0 } from '../contexts/useAuthContext';
import { LogoutBtn } from "../components/ui";
import { getAccessRequestsCount } from '../services';
import logo from "../../images/logo.svg";
import smartAlarmsLogo from "../../images/smart-alarm-logo.svg";

interface NavBarProps { onToggleSidebar?: () => void, isSidebarCollapsed?: boolean }

type NavbarUserProfile = {
    firstName?: string;
    lastName?: string;
    name?: string;
};

// Helper hook to compute active route matches
const useRouteMatches = () => {
    const rootMatchObj = useMatch({ path: '/', end: true });
    const manageMatchA = useMatch('/manage/*');
    const manageMatchB = useMatch('/manage');
    const commsDetailsMatchA = useMatch('/comms/*');
    const commsDetailsMatchB = useMatch('/comms');
    const analyzeMatchA = useMatch('/analyze/*');
    const analyzeMatchB = useMatch('/analyze');
    const dashboardMatchA = useMatch('/dashboard/*');
    const dashboardMatchB = useMatch('/dashboard');
    const alarmEstateMatchA = useMatch('/alarm-estate/*');
    const alarmEstateMatchB = useMatch('/alarm-estate');
    const reportsMatchA = useMatch('/reports/*');
    const reportsMatchB = useMatch('/reports');

    return {
        isRootMatch: !!rootMatchObj,
        isManageMatch: !!manageMatchA || !!manageMatchB,
        isAnalyzeMatch: !!analyzeMatchA || !!analyzeMatchB,
        isDashboardMatch: !!dashboardMatchA || !!dashboardMatchB,
        isAlarmEstateMatch: !!alarmEstateMatchA || !!alarmEstateMatchB,
        isCommsDetailsMatch: !!commsDetailsMatchA || !!commsDetailsMatchB,
        isReportsMatch: !!reportsMatchA || !!reportsMatchB,
    };
};

// Navigation options component
const NavOptions: React.FC<{ matches: ReturnType<typeof useRouteMatches>, isDropdownClosed: boolean }> = ({ matches, isDropdownClosed }) => (
    <>
        <li className={`nav-bar__option--monitor ${matches.isRootMatch ? 'is-active' : ''}`}>
            <Link to="/" aria-current={matches.isRootMatch ? 'page' : undefined}>Monitor</Link>
        </li>
        <li className={`nav-bar__option--manage ${matches.isManageMatch ? 'is-active' : ''} ${isDropdownClosed ? 'dropdown-closed' : ''}`}>
            <Link to="/manage" aria-current={matches.isManageMatch ? 'page' : undefined}>Manage</Link>
            <ul className="dropdown">
                <li className="nav-bar__option--buildings"><Link to="/manage/buildings">Buildings</Link></li>
                <li className="nav-bar__option--distribution-groups"><Link to="/manage/distribution-groups">Distribution Groups</Link></li>
                <li className="nav-bar__option--attachment-groups"><Link to="/manage/attachment-groups">Attachment Groups</Link></li>
                <li className="nav-bar__option--csh-groups"><Link to="/manage/csh-groups">CSH Groups</Link></li>
                <li className="nav-bar__option--schedules"><Link to="/manage/schedules">Schedules</Link></li>
                <li className="nav-bar__option--rules"><Link to="/manage/rules">Rules</Link></li>
                <li className="nav-bar__option--pending"><Link to="/manage/pending">Pending Sites</Link></li>
                <li className="nav-bar__option--category"><Link to="/manage/categories">Categories</Link></li>
            </ul>
        </li>
        <li className={`nav-bar__option--comms-details ${matches.isCommsDetailsMatch ? 'is-active' : ''}`}><Link to="/comms">Comms</Link></li>
        
        <li className={`nav-bar__option--dashboard ${matches.isDashboardMatch ? 'is-active' : ''}`}>
            <Link to="/dashboard" aria-current={matches.isDashboardMatch ? 'page' : undefined}>Dashboard</Link>
        </li>
        
        <li className={`nav-bar__option--reports ${matches.isReportsMatch ? 'is-active' : ''}`}><Link to="/reports">Reports</Link></li>
    </>
);

// Profile section component
const NavProfile: React.FC<{ userProfile: NavbarUserProfile, canManageAccessRequests: boolean, accessRequestsCount: number }> = ({ userProfile, canManageAccessRequests, accessRequestsCount }) => (
    <>
        {canManageAccessRequests && (
            <li className="nav-bar__notifications">
                <Link to="/admin/request" aria-label="Access Requests">
                    <figure>
                        {accessRequestsCount > 0 && (
                            <span className="notification-badge">
                                {accessRequestsCount}
                            </span>
                        )}
                    </figure>
                </Link>
            </li>
        )}
        <li className="nav-bar__user">
            {userProfile.firstName ? `${userProfile.firstName} ${userProfile.lastName || ''}`.trim() : userProfile.name}
            {canManageAccessRequests && <span className="admin-badge">ADMIN</span>}
        </li>
        <li className="nav-bar__logout">
            <LogoutBtn />
        </li>
    </>
);

const NavBar: React.FC<NavBarProps> = ({ onToggleSidebar, isSidebarCollapsed }) => {
    const { userProfile, userRole, user, isAuthenticated, isNormalUser, isLoading } = useAuth0();
    const location = useLocation();
    const matches = useRouteMatches();
    const [isDropdownClosed, setIsDropdownClosed] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [accessRequestsCount, setAccessRequestsCount] = useState(0);
    const canManageAccessRequests = !isLoading && isAuthenticated && !!userProfile && userRole?.role === 'Admin';
    const userId = user?.profile.sub;

    const handleToggleSidebar = () => {
        setIsCollapsed(!isCollapsed);
        if (onToggleSidebar) {
            onToggleSidebar();
        }
    };

    useEffect(() => {
        setAccessRequestsCount(0);
        if (!canManageAccessRequests) {
            return;
        }

        let cancelled = false;
        const fetchAccessRequestsCount = async () => {
            try {
                const response = await getAccessRequestsCount();
                if (cancelled) return;
                if (response.success && response.data) {
                    setAccessRequestsCount(response.data.count);
                } else {
                    console.info('Failed to fetch access requests count:', response.error);
                }
            } catch (error: unknown) {
                if (!cancelled) console.info('Failed to fetch access requests count:', error);
            }
        };

        void fetchAccessRequestsCount();
        const handleAccessRequestSubmitted = () => {
            void fetchAccessRequestsCount();
        };

        window.addEventListener('accessRequestSubmitted', handleAccessRequestSubmitted);
        return () => {
            cancelled = true;
            window.removeEventListener('accessRequestSubmitted', handleAccessRequestSubmitted);
        };
    }, [canManageAccessRequests, userId]);

    React.useEffect(() => {
        setIsDropdownClosed(true);
    const t = globalThis.setTimeout(() => setIsDropdownClosed(false), 250);
    return () => globalThis.clearTimeout(t);
    }, [location.pathname]);

    // Don't render anything while loading OR when authenticated but profile not yet loaded
    // This prevents flash of unauthenticated navbar during profile fetch
    if (isLoading || (isAuthenticated && !userProfile)) {
        return (
            <nav className="nav-bar">
                <ul className="nav-bar__branding"></ul>
                <ul className="nav-bar__options"></ul>
                <ul className="nav-bar__profile"></ul>
            </nav>
        );
    }

    return (
        <nav className="nav-bar">
            <ul className="nav-bar__branding">
                {isAuthenticated && userProfile ? (
                    <>
                        <li className="nav-bar__logo">
                            <Link to="/">
                                <img src={smartAlarmsLogo} className="smart-alarm-logo" alt="SmartAlarm Logo" />
                            </Link>
                        </li>
                        <li className="nav-bar__menu">
                            <button 
                                className={`btn__menu ${isCollapsed || isSidebarCollapsed ? 'is-collapsed' : ''}`} 
                                type="button" 
                                onClick={handleToggleSidebar} 
                                aria-label="Toggle navigation menu"
                            >
                            </button>
                        </li>
                    </>
                ) : (
                    <Link to="/">
                        <img src={logo} className="logo" alt="Schneider Electric Logo" />
                    </Link>
                )}
            </ul>
            <ul className="nav-bar__options">
                {isAuthenticated && userProfile && !isNormalUser() && <NavOptions matches={matches} isDropdownClosed={isDropdownClosed} />}
            </ul>
            <ul className="nav-bar__profile">
                {isAuthenticated && userProfile ? (
                    <NavProfile userProfile={userProfile} canManageAccessRequests={canManageAccessRequests} accessRequestsCount={accessRequestsCount} />
                ) : (
                    <li className="nav-bar__user">Welcome to SmartAlarm</li>
                )}
            </ul>
        </nav>
    );
};

export default NavBar;
